import { useEffect, useState } from 'react';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import type { BoNotice, BoNoticeInput, BoNoticeKind, BoNoticeLevel } from '../../client/api';
import { cn } from '../../utils/cn';
import {
  BoPage,
  Section,
  ErrorBanner,
  btnGhost,
  btnPrimary,
  errMsg,
  formatDateTime,
  inputCls,
} from './ui';

// =============================================================================
// BO · Notices & banners (NBL_REQ_809 / HNBL-117) — platform-wide announcement
// CRUD with a display window. Soft-deleted rows drop out of the list.
// =============================================================================

const LEVELS: BoNoticeLevel[] = ['info', 'warning', 'critical'];
const KINDS: BoNoticeKind[] = ['notice', 'banner'];

const LEVEL_LABEL: Record<BoNoticeLevel, string> = {
  info: '정보',
  warning: '경고',
  critical: '긴급',
};
const KIND_LABEL: Record<BoNoticeKind, string> = {
  notice: '공지',
  banner: '배너',
};
const LEVEL_CLS: Record<BoNoticeLevel, string> = {
  info: 'bg-accent-soft text-accent',
  warning: 'bg-[#f5a623]/15 text-[#f5a623]',
  critical: 'bg-warning/10 text-warning',
};

/** ISO → value for <input type="datetime-local"> (local time, no seconds). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** datetime-local value → ISO; empty → undefined. */
function localInputToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

type FormState = {
  title: string;
  kind: BoNoticeKind;
  level: BoNoticeLevel;
  body: string;
  startsAt: string;
  endsAt: string;
};
const emptyForm: FormState = {
  title: '',
  kind: 'notice',
  level: 'info',
  body: '',
  startsAt: '',
  endsAt: '',
};

function isActive(n: BoNotice, now: number): boolean {
  const start = new Date(n.startsAt).getTime();
  if (start > now) return false;
  if (n.endsAt && new Date(n.endsAt).getTime() <= now) return false;
  return true;
}

export function Notices(): JSX.Element {
  const [notices, setNotices] = useState<BoNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setNotices(await api.boListNotices());
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }
  function reset() {
    setEditingId(null);
    setForm(emptyForm);
  }
  function startEdit(n: BoNotice) {
    setEditingId(n.id);
    setForm({
      title: n.title,
      kind: n.kind,
      level: n.level,
      body: n.body ?? '',
      startsAt: isoToLocalInput(n.startsAt),
      endsAt: isoToLocalInput(n.endsAt),
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    setErr(null);
    const payload: BoNoticeInput = {
      title: form.title.trim(),
      kind: form.kind,
      level: form.level,
      body: form.body.trim() || null,
    };
    const startsAt = localInputToIso(form.startsAt);
    if (startsAt) payload.startsAt = startsAt;
    payload.endsAt = localInputToIso(form.endsAt) ?? null;
    try {
      if (editingId) await api.boUpdateNotice(editingId, payload);
      else await api.boCreateNotice(payload);
      reset();
      await load();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(n: BoNotice) {
    if (!confirm(`'${n.title}'을 삭제하시겠습니까?`)) return;
    setErr(null);
    try {
      await api.boDeleteNotice(n.id);
      if (editingId === n.id) reset();
      await load();
    } catch (e) {
      setErr(errMsg(e));
    }
  }

  const now = Date.now();

  return (
    <BoPage title="전체 공지·배너" subtitle="운영사 전용 — 모든 테넌트에 노출되는 플랫폼 공지/배너 관리.">
      <ErrorBanner message={err} />
      {loading ? <ContentSkeleton label="공지·배너를 불러오는 중…" variant="split" /> : <>
      <Section title={editingId ? '공지 수정' : '공지/배너 작성'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-1 flex-col gap-1" style={{ minWidth: 264 }}>
              <label className="text-[13px] font-medium text-text-dim">제목 *</label>
              <input className={cn(inputCls, 'w-full')} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="정기 점검 안내" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">종류</label>
              <select className={cn(inputCls, 'w-28')} value={form.kind} onChange={(e) => set('kind', e.target.value as BoNoticeKind)}>
                {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">수준</label>
              <select className={cn(inputCls, 'w-28')} value={form.level} onChange={(e) => set('level', e.target.value as BoNoticeLevel)}>
                {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[13px] font-medium text-text-dim">본문 (선택)</label>
            <textarea className={cn(inputCls, 'min-h-[77px] w-full resize-y')} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="상세 내용…" />
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">노출 시작</label>
              <input type="datetime-local" className={cn(inputCls, 'w-52')} value={form.startsAt} onChange={(e) => set('startsAt', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">노출 종료 (선택)</label>
              <input type="datetime-local" className={cn(inputCls, 'w-52')} value={form.endsAt} onChange={(e) => set('endsAt', e.target.value)} />
            </div>
            <button type="submit" disabled={saving || !form.title.trim()} className={btnPrimary}>
              {saving ? '저장 중…' : editingId ? '수정 저장' : '게시'}
            </button>
            {editingId && <button type="button" className={btnGhost} onClick={reset}>취소</button>}
          </div>
          <p className="text-[13px] text-text-faint">시작 시각을 비우면 즉시 노출됩니다. 종료를 비우면 무기한 노출됩니다.</p>
        </form>
      </Section>

      <Section title="공지 목록">
        {notices.length === 0 ? (
          <p className="py-6 text-center text-[14px] text-text-faint">공지가 없습니다.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {notices.map((n) => {
              const active = isActive(n, now);
              return (
                <li key={n.id} className="flex items-start gap-3 rounded border border-line bg-panel-2/40 px-3 py-2.5">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={cn('rounded px-1.5 py-0.5 text-[12px] font-medium', LEVEL_CLS[n.level])}>
                        {KIND_LABEL[n.kind]} · {LEVEL_LABEL[n.level]}
                      </span>
                      <span className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px]', active ? 'bg-green/10 text-green' : 'bg-panel-3 text-text-faint')}>
                        <span className={cn('h-1.5 w-1.5 rounded-full', active ? 'bg-green' : 'bg-text-faint')} />
                        {active ? '노출 중' : '비노출'}
                      </span>
                      <span className="truncate text-[15px] font-medium text-text">{n.title}</span>
                    </div>
                    {n.body && <p className="truncate text-[14px] text-text-dim">{n.body}</p>}
                    <p className="font-mono text-[12px] text-text-faint">
                      {formatDateTime(n.startsAt)} → {n.endsAt ? formatDateTime(n.endsAt) : '무기한'}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <button type="button" className={btnGhost} onClick={() => startEdit(n)}>수정</button>
                    <button type="button" className={cn(btnGhost, 'text-warning')} onClick={() => void handleDelete(n)}>삭제</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      </>}
    </BoPage>
  );
}
